import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CheckboxInput,
  DropdownInput,
  EmptyState,
  Form,
  Icon,
  TextInput,
  useForm,
  VariableTextInput,
} from "@validereinc/common-components";
import { TemplatedReportAdapter } from "@validereinc/domain";
import React, { useEffect, useMemo, useRef } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";

const WorkflowConfigurationRunTemplatedReportDetails = ({
  stepId,
}: {
  stepId: string;
}) => {
  const {
    setRunTemplatedReport,
    setRunTemplatedReportIsValid,
    getRunTemplatedReportFormValues,
  } = useWorkflowConfigurationFormStore();

  const { getAvailableStepVariables } = useWorkflowGraphState();

  const storeValues = getRunTemplatedReportFormValues();
  const isInitializedRef = useRef(false);

  const form = useForm({
    values: storeValues,
  });

  const selectedTemplateName = form.watch("templated_report_name");
  const availableVariables = getAvailableStepVariables(stepId);

  // Initialize form only once when store values are first available
  useEffect(() => {
    if (storeValues && !isInitializedRef.current) {
      form.reset(storeValues);
      isInitializedRef.current = true;
    }
  }, [storeValues, form]);

  // Reset initialization flag when stepId changes (new node selected)
  useEffect(() => {
    isInitializedRef.current = false;
  }, [stepId]);

  // Fetch templated reports list
  const templatedReportsQuery = useQuery({
    queryKey: ["templated-reports", "company", "getList"],
    queryFn: () => TemplatedReportAdapter.company.getList({}),
  });

  const templatedReportOptions = useMemo(() => {
    return (templatedReportsQuery.data?.data ?? []).map((report) => ({
      label: report.display_name,
      value: report.name,
    }));
  }, [templatedReportsQuery.data?.data]);

  // Fetch selected template details to get input schema
  const selectedTemplateQuery = useQuery({
    queryKey: ["templated-report", selectedTemplateName],
    queryFn: () => TemplatedReportAdapter.getOne({ id: selectedTemplateName }),
    enabled: !!selectedTemplateName,
  });

  // Memoize templateInputSchema to prevent infinite loops
  const templateInputSchema = useMemo(() => {
    return selectedTemplateQuery.data?.data?.input_schema ?? {};
  }, [selectedTemplateQuery.data?.data?.input_schema]);

  const [name, templatedReportName, enableNewVersion, reportInput] = form.watch(
    ["name", "templated_report_name", "enable_new_version", "report_input"]
  );

  useEffect(() => {
    if (!isInitializedRef.current) return;

    if (name && templatedReportName) {
      setRunTemplatedReport(form.getValues() as any);
    }
  }, [
    name,
    templatedReportName,
    enableNewVersion,
    reportInput,
    setRunTemplatedReport,
    form,
  ]);

  useEffect(() => {
    setRunTemplatedReportIsValid(form.formState.isValid);
  }, [form.formState.isValid, setRunTemplatedReportIsValid]);

  // Reset report_input when template changes - memoized to prevent loops
  useEffect(() => {
    if (selectedTemplateName && Object.keys(templateInputSchema).length > 0) {
      const currentReportInput = form.getValues("report_input") ?? {};

      // Create default values for new fields, preserve existing values
      const updatedReportInput = Object.keys(templateInputSchema).reduce<
        Record<string, any>
      >((acc, key) => {
        // Keep existing value if it exists, otherwise use empty string as default
        acc[key] =
          currentReportInput[key] !== undefined ? currentReportInput[key] : "";
        return acc;
      }, {});

      // Only update if the structure has changed
      const currentKeys = Object.keys(currentReportInput);
      const newKeys = Object.keys(updatedReportInput);
      const hasChanged =
        currentKeys.length !== newKeys.length ||
        !currentKeys.every((key) => newKeys.includes(key));

      if (hasChanged) {
        form.setValue("report_input", updatedReportInput);
      }
    }
  }, [selectedTemplateName, templateInputSchema]);

  const reportInputFields = useMemo(() => {
    if (!selectedTemplateName || !templateInputSchema) return [];

    return Object.entries(templateInputSchema).map(([key, inputDef]) => ({
      key,
      label: inputDef.display_name ?? key,
      description: inputDef.description,
      required: inputDef.is_required ?? false,
      dataType: inputDef.data_type,
      pickListValues: inputDef.pick_list_values,
    }));
  }, [templateInputSchema, selectedTemplateName]);

  const renderReportInputField = (field: {
    key: string;
    label: string;
    description?: string;
    required: boolean;
    dataType: string;
    pickListValues?: string[];
  }) => {
    const fieldName = `report_input.${field.key}`;

    switch (field.dataType) {
      case "pick-list":
        return (
          <DropdownInput
            key={field.key}
            name={fieldName}
            label={field.label}
            isRequired={field.required}
            options={
              field.pickListValues?.map((value) => ({
                label: value,
                value,
              })) ?? []
            }
            labelKey="label"
            valueKey="value"
            description={field.description}
          />
        );
      case "boolean":
        return (
          <CheckboxInput
            key={field.key}
            name={fieldName}
            label={field.label}
            description={field.description}
          />
        );
      case "number":
      case "integer":
        return (
          <TextInput
            key={field.key}
            name={fieldName}
            label={field.label}
            type="number"
            isRequired={field.required}
            description={field.description}
          />
        );
      default:
        return (
          <VariableTextInput
            key={field.key}
            name={fieldName}
            label={field.label}
            isRequired={field.required}
            suggestions={availableVariables}
            type="text"
            description={field.description}
          />
        );
    }
  };

  return (
    <Form {...form}>
      <VariableTextInput
        name="name"
        label="Report Name"
        isRequired
        suggestions={availableVariables}
        type="text"
        description="Human-readable name for this report execution"
      />

      <DropdownInput
        name="templated_report_name"
        label="Template Name"
        isRequired
        options={templatedReportOptions}
        labelKey="label"
        valueKey="value"
        isLoading={templatedReportsQuery.isLoading}
        description="Select the templated report to execute"
      />

      <CheckboxInput
        name="enable_new_version"
        label="Enable New Version"
        description="Create a new version of the report if it already exists"
      />

      {selectedTemplateName && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <strong>Report Input Parameters</strong>
          </div>

          {reportInputFields.length === 0 ? (
            <EmptyState
              title="No input parameters"
              suggestion="This template does not require any input parameters."
              icon={<Icon variant="files" />}
              style={{ padding: 16 }}
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {reportInputFields.map(renderReportInputField)}
            </div>
          )}
        </Card>
      )}
    </Form>
  );
};

export default WorkflowConfigurationRunTemplatedReportDetails;
